import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import type { AppController, ExecuteAction, FoodSearchResult, RecipeDetails } from "../types";
import { TrashIcon } from "../components/TrashIcon";
import { RecipeEditor } from "./RecipeEditor";

interface RecipeLibraryProps {
  controller: AppController;
  execute: ExecuteAction;
  onCancel: () => void;
}

export function RecipeLibrary({ controller, execute, onCancel }: RecipeLibraryProps) {
  const [recipes, setRecipes] = useState<RecipeDetails[]>([]);
  const [editing, setEditing] = useState<RecipeDetails | "new">();
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string>();
  const dialogRef = useRef<HTMLElement>(null);

  async function loadRecipes() {
    setLoading(true);
    const result = await execute(() => controller.listRecipes(), {
      errorMessage: "Your recipes could not be loaded. Please try again.",
    });
    if (result.ok) setRecipes(result.value);
    setLoading(false);
  }

  useEffect(() => {
    void loadRecipes();
  }, []);

  useEffect(() => {
    if (editing) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialogRef.current?.querySelector<HTMLElement>("button:not(:disabled)")?.focus();
    return () => previouslyFocused?.focus();
  }, [editing]);

  function saved(_recipe: FoodSearchResult) {
    setEditing(undefined);
    void loadRecipes();
  }

  async function removeRecipe(recipe: RecipeDetails) {
    if (!window.confirm(`Delete ${recipe.name}? Existing diary entries logged from it will be kept.`)) return;
    setDeletingId(recipe.id);
    const result = await execute(
      () => controller.deleteRecipe(recipe.id),
      { successMessage: `${recipe.name} deleted.` },
    );
    setDeletingId(undefined);
    if (result.ok) setRecipes((current) => current.filter((candidate) => candidate.id !== recipe.id));
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
    }
  }

  if (editing) {
    return (
      <RecipeEditor
        key={editing === "new" ? "new" : editing.id}
        controller={controller}
        execute={execute}
        initialRecipe={editing === "new" ? undefined : editing}
        onCancel={() => setEditing(undefined)}
        onSaved={saved}
      />
    );
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section
        ref={dialogRef}
        className="modal-sheet recipe-library"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recipe-library-heading"
        aria-busy={loading}
        onKeyDown={handleKeyDown}
      >
        <div className="modal-sheet__handle" aria-hidden="true" />
        <div className="modal-sheet__heading">
          <div>
            <p className="eyebrow">Your recipes</p>
            <h2 id="recipe-library-heading">Review recipes</h2>
            <p className="quantity-dialog__basis">Review ingredients and update recipe yields or quantities.</p>
          </div>
          <button className="icon-button" type="button" onClick={onCancel} aria-label="Close recipe library">x</button>
        </div>

        <div className="button-row recipe-library__toolbar">
          <button className="button button--primary" type="button" onClick={() => setEditing("new")}>Create recipe</button>
        </div>

        {loading ? (
          <div className="empty-state" role="status"><p>Loading recipes…</p></div>
        ) : recipes.length === 0 ? (
          <div className="empty-state">
            <strong>No recipes yet</strong>
            <p>Create a recipe to combine foods into reusable servings.</p>
          </div>
        ) : (
          <ul className="recipe-library__list">
            {recipes.map((recipe) => (
              <li className="recipe-library__card" key={recipe.id}>
                <div className="recipe-library__card-top">
                  <button
                    className="recipe-library__edit-card"
                    type="button"
                    onClick={() => setEditing(recipe)}
                    disabled={deletingId === recipe.id}
                    aria-label={`Edit ${recipe.name}`}
                  >
                    <span className="recipe-library__heading">
                      <span>
                        <strong>{recipe.name}</strong>
                        <span>Makes {formatNumber(recipe.servingCount)} {recipe.servingName}</span>
                      </span>
                      <strong>{formatNumber(recipe.nutrition.calories)} kcal / {recipe.servingName}</strong>
                    </span>
                    <span className="macro-summary" aria-label={`${recipe.name} nutrition per ${recipe.servingName}`}>
                      <span>P {formatGrams(recipe.nutrition.proteinGrams)}</span>
                      <span>C {formatGrams(recipe.nutrition.carbohydrateGrams)}</span>
                      <span>F {formatGrams(recipe.nutrition.fatGrams)}</span>
                    </span>
                  </button>
                  <button
                    className="icon-button icon-button--danger recipe-library__delete"
                    type="button"
                    onClick={() => void removeRecipe(recipe)}
                    disabled={deletingId === recipe.id}
                    aria-label={`Delete ${recipe.name}`}
                    title={`Delete ${recipe.name}`}
                  >
                    <TrashIcon />
                  </button>
                </div>
                <details className="details-panel details-panel--nested">
                  <summary>{recipe.ingredients.length} {recipe.ingredients.length === 1 ? "ingredient" : "ingredients"}</summary>
                  <ul className="recipe-library__ingredients">
                    {recipe.ingredients.map((ingredient, index) => (
                      <li key={`${ingredient.id}-${index}`}>
                        <span>{ingredient.name}</span>
                        <strong>{formatNumber(ingredient.amount)} {unitLabel(ingredient)}</strong>
                      </li>
                    ))}
                  </ul>
                </details>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function unitLabel(ingredient: RecipeDetails["ingredients"][number]): string {
  if (ingredient.basisUnit === "g" || ingredient.basisUnit === "ml") return ingredient.basisUnit;
  return ingredient.servingName || "serving";
}

function formatNumber(value: number): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatGrams(value: number): string {
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} g`;
}
